import { useGetAlphaBscTokenHistoryQuery } from "@/store/api";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip, ReferenceLine } from "recharts";

/**
 * Tiny sparkline of a token's price history. Used inline in position
 * tables to give a quick visual on direction since entry.
 *
 * - 32px tall, fits in a table cell
 * - Auto-scaled Y axis (just shows shape, not exact values)
 * - Optional reference line at entry price (green if above, red if below)
 * - Tooltip shows price on hover
 */
export function TokenSparkline({
  tokenId,
  entryPrice,
  width = 120,
  height = 32,
}: {
  tokenId: number;
  entryPrice?: number;
  width?: number;
  height?: number;
}) {
  const { data, isLoading } = useGetAlphaBscTokenHistoryQuery(
    { token_id: tokenId, limit: 40 },
    { pollingInterval: 60000 }
  );

  if (isLoading) {
    return <div style={{ width, height }} className="text-[10px] text-muted/50 flex items-center justify-center">…</div>;
  }
  if (!data || data.length < 2) {
    // Loaded but no history yet (new token / not yet snapshotted).
    return <div style={{ width, height }} className="text-[10px] text-muted/50 flex items-center justify-center">—</div>;
  }

  // Reverse so chronological (oldest → newest, left → right)
  const series = [...data].reverse().map((s: any, i: number) => ({
    i,
    p: Number(s.price_usd ?? 0),
  })).filter((s) => s.p > 0);

  if (series.length < 2) {
    return <div style={{ width, height }} className="text-[10px] text-muted/50 flex items-center justify-center">—</div>;
  }

  const cur = series[series.length - 1].p;
  const aboveEntry = entryPrice && cur > entryPrice;
  const color = entryPrice
    ? (aboveEntry ? "#22c55e" : "#ef4444")
    : "#64748b";

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Tooltip
            cursor={false}
            contentStyle={{
              background: "#0c0c0c",
              border: "1px solid #333",
              fontSize: "10px",
              padding: "2px 4px",
            }}
            formatter={(v: any) => [`$${Number(v).toPrecision(4)}`, "price"]}
            labelFormatter={() => ""}
          />
          {entryPrice && (
            <ReferenceLine
              y={entryPrice}
              stroke="#94a3b8"
              strokeDasharray="2 2"
              strokeWidth={1}
            />
          )}
          <Line
            type="monotone"
            dataKey="p"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
